import { api, authRefreshApi } from '../../../services/api';
import type { PresignedImageUpload, ProfileImageViewUrlPayload } from '../../uploads/types';
import type {
  ActionResult,
  ApiResponse,
  ChangePasswordRequest,
  DeleteMyAccountPayload,
  ConfirmEmailChangePayload,
  ConfirmForgotPasswordRequest,
  ConfirmSignUpRequest,
  ForgotPasswordRequest,
  RequestEmailChangePayload,
  ResendConfirmationRequest,
  GetUserData,
  RefreshTokenData,
  RefreshTokenRequest,
  SignInRequest,
  SignInResponse,
  SignOutRequest,
  SignUpRequest,
  SignUpResponse,
  UpdateUserRequest,
  CompleteInvitePasswordRequest,
} from '../types/auth';

/** Auth paths from `backend-docs.md`; host is `NEXT_PUBLIC_API_URL` (see `api` client). */
const paths = {
  signIn: '/auth/signin',
  signUp: '/auth/signup',
  confirmSignUp: '/auth/confirm-signup',
  forgotPassword: '/auth/forgot-password',
  confirmForgotPassword: '/auth/confirm-forgot-password',
  completeInvitePassword: '/auth/complete-invite-password',
  refreshToken: '/auth/refresh-token',
  signOut: '/auth/signout',
  user: '/auth/user',
  changePassword: '/auth/change-password',
  changeEmail: '/auth/change-email',
  confirmEmailChange: '/auth/confirm-email-change',
  deleteAccount: '/auth/delete-account',
  /** Must match `serverless/services/auth/serverless.yml` (`resendConfirmationCode`). */
  resendConfirmationCode: '/auth/resend-confirmation-code',
} as const;

export async function signIn(payload: SignInRequest): Promise<SignInResponse> {
  const { data } = await api.post<SignInResponse>(paths.signIn, payload);
  return data;
}

export async function signUp(payload: SignUpRequest): Promise<SignUpResponse> {
  const { data } = await api.post<SignUpResponse>(paths.signUp, payload);
  return data;
}

export async function confirmSignUp(
  payload: ConfirmSignUpRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.confirmSignUp,
    { email: payload.email, code: payload.code.trim() },
  );
  return data;
}

export async function forgotPassword(
  payload: ForgotPasswordRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.forgotPassword,
    payload,
  );
  return data;
}

export async function confirmForgotPassword(
  payload: ConfirmForgotPasswordRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.confirmForgotPassword,
    payload,
  );
  return data;
}

export async function completeInvitePassword(
  payload: CompleteInvitePasswordRequest,
): Promise<SignInResponse> {
  const { data } = await api.post<SignInResponse>(
    paths.completeInvitePassword,
    payload,
  );
  return data;
}

export async function refreshToken(
  payload: RefreshTokenRequest,
): Promise<ApiResponse<RefreshTokenData>> {
  const { data } = await authRefreshApi.post<ApiResponse<RefreshTokenData>>(
    paths.refreshToken,
    payload,
  );
  return data;
}

export async function signOut(
  payload: SignOutRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.signOut,
    payload,
  );
  return data;
}

export async function getCurrentUser(
  accessToken: string,
): Promise<ApiResponse<GetUserData>> {
  const { data } = await api.get<ApiResponse<GetUserData>>(paths.user, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return data;
}

export async function updateCurrentUser(
  accessToken: string,
  payload: UpdateUserRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.put<ApiResponse<ActionResult>>(
    paths.user,
    payload,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return data;
}

/** `PUT /auth/user` — flat body matches `updateUserProfileSchema` (firstName, lastName, …). */
export async function updateProfileNames(
  accessToken: string,
  body: { firstName: string; lastName: string },
): Promise<ApiResponse<Record<string, unknown>>> {
  const { data } = await api.put<ApiResponse<Record<string, unknown>>>(
    paths.user,
    body,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return data;
}

export async function changePassword(
  accessToken: string,
  payload: ChangePasswordRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.changePassword,
    {
      currentPassword: payload.oldPassword,
      newPassword: payload.newPassword,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return data;
}

export async function deleteMyAccount(
  accessToken: string,
  payload: DeleteMyAccountPayload,
): Promise<ApiResponse<{ message: string }>> {
  const { data } = await api.post<ApiResponse<{ message: string }>>(
    paths.deleteAccount,
    { password: payload.password },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  return data;
}

export async function requestEmailChange(
  accessToken: string,
  body: RequestEmailChangePayload,
): Promise<
  ApiResponse<{ message?: string; newEmail?: string }>
> {
  const { data } = await api.post<
    ApiResponse<{ message?: string; newEmail?: string }>
  >(paths.changeEmail, body, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return data;
}

export async function confirmEmailChange(
  accessToken: string,
  body: ConfirmEmailChangePayload,
): Promise<
  ApiResponse<{
    message?: string;
    oldEmail?: string;
    newEmail?: string;
    emailVerified?: boolean;
  }>
> {
  const { data } = await api.post<
    ApiResponse<{
      message?: string;
      oldEmail?: string;
      newEmail?: string;
      emailVerified?: boolean;
    }>
  >(paths.confirmEmailChange, body, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return data;
}

export async function resendConfirmationCode(
  payload: ResendConfirmationRequest,
): Promise<ApiResponse<ActionResult>> {
  const { data } = await api.post<ApiResponse<ActionResult>>(
    paths.resendConfirmationCode,
    { email: payload.email.trim().toLowerCase() },
  );
  return data;
}

type ApiDataEnvelope<T> = { success: boolean; data: T };

function unwrapApiData<T>(res: { data: ApiDataEnvelope<T> }): T {
  return res.data.data;
}

export async function postProfileImagePresign(body: {
  contentType: 'image/jpeg' | 'image/png';
  fileSize?: number;
}): Promise<PresignedImageUpload> {
  const res = await api.post<ApiDataEnvelope<PresignedImageUpload>>(
    '/auth/profile-image/presign',
    body,
  );
  return unwrapApiData(res);
}

export async function postProfileImageConfirm(body: {
  key: string;
  contentType: 'image/jpeg' | 'image/png';
}): Promise<Record<string, unknown>> {
  const res = await api.post<ApiDataEnvelope<Record<string, unknown>>>(
    '/auth/profile-image/confirm',
    body,
  );
  return unwrapApiData(res);
}

export async function deleteProfileImage(): Promise<Record<string, unknown>> {
  const res = await api.delete<ApiDataEnvelope<Record<string, unknown>>>(
    '/auth/profile-image',
  );
  return unwrapApiData(res);
}

export async function getProfileImageViewUrl(): Promise<ProfileImageViewUrlPayload> {
  const res = await api.get<ApiDataEnvelope<ProfileImageViewUrlPayload>>(
    '/auth/profile-image/view-url',
  );
  return unwrapApiData(res);
}
